/**
 * Every wording of the claim that the COO costs nothing (#1741). It is the claim about the COO that is barred, not
 * the word "free": "Start free" and "Free: your COO plus 1 teammate" say the plan is free, and stay allowed.
 */
const COO_FREE_CLAIM_PATTERNS: readonly RegExp[] = [
    // "The COO is free", "Your COO is free on every plan", "the COO comes free", "your COO is always free"
    /\b(?:(?:the|your)\s+)?COO\s+(?:is|comes)\s+(?:(?:always|completely|totally|entirely|included)\s+)?(?:for\s+)?free\b/,
    // "The COO costs nothing", "Your COO costs nothing to start", "the COO costs $0"
    /\b(?:(?:the|your)\s+)?COO\s+costs?\s+(?:nothing|\$0|zero)\b/,
    // "Get your COO for free", "your COO at no extra cost", "the COO free of charge"
    /\b(?:(?:the|your)\s+)?COO\s+(?:for\s+free|at\s+no\s+(?:extra\s+)?(?:cost|charge)|free\s+of\s+charge)\b/,
    // "Meet your free COO"
    /\bfree\s+COO\b/,
    // The COO by name, written out or interpolated: "Casey is free on every plan", "{{ name }} is free on every plan".
    // The user names the COO, so shipped source holds the name as `{{ … }}` (a Vue template) or `${…}` (a template
    // literal), never as a word.
    /(?:\$\{[^}]*\}|\{\{[^}]*\}\}|\b[a-z][\w'\u2019-]*)\s+is\s+free\s+on\s+every\s+plan\b/,
]

/**
 * The COO claim as one pattern, so a sentence that fits more than one wording ("The COO is free on every plan") is
 * reported once, as written. Words may be split by any whitespace, and letter case is ignored.
 */
const COO_FREE_CLAIM = new RegExp(COO_FREE_CLAIM_PATTERNS.map(pattern => `(?:${pattern.source})`).join('|'), 'gi')

/**
 * Every phrase that tells a user a model comes with the product (#1717). Teammates run on the user's own AI
 * account, so none of these may appear in user-facing copy. Words may be split by any whitespace, so a phrase
 * wrapped across two lines of a template is still found.
 */
const INCLUDED_MODEL_CLAIMS: readonly RegExp[] = [
    /\bmodels?\s+included\b/gi,
    /\bincluded\s+models?\b/gi,
    /\bbuilt[-\s]?in\s+models?\b/gi,
    /\b(?:free|trial)\s+credits?\b/gi,
    /\bno\s+(?:api\s+)?keys?\s+(?:needed|required)\b/gi,
    COO_FREE_CLAIM,
]

/** Each match of `pattern` in `text`, as written. */
function matchesOf(pattern: RegExp, text: string): string[] {
    return Array.from(text.matchAll(pattern), match => match[0])
}

/**
 * The phrases in user-facing copy that imply a model is included. Returns each one found in `text`, in the order
 * the patterns are listed, so a sweep of the portal and the built-in tasks can fail on any of them.
 */
export function findIncludedModelClaims(text: string): string[] {
    return INCLUDED_MODEL_CLAIMS.flatMap(pattern => matchesOf(pattern, text))
}

/**
 * The phrases that say the COO costs nothing, in any wording. A narrower sweep than `findIncludedModelClaims`, for the
 * sources where only this claim is barred.
 */
export function findCooFreeClaims(text: string): string[] {
    return matchesOf(COO_FREE_CLAIM, text)
}
